import React, { useState, useEffect, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { calendarAPI, coachesAPI, locationsAPI, lessonsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar, User, MapPin, Clock, CheckCircle, Edit, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const LessonCalendar = () => {
  const { canEdit } = useAuth();
  const [events, setEvents] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleCoach, setRescheduleCoach] = useState('');
  const [rescheduleLocation, setRescheduleLocation] = useState('');
  const [filterCoach, setFilterCoach] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (filterCoach !== 'all') params.coach_id = filterCoach;
      if (filterLocation !== 'all') params.location_id = filterLocation;

      const [eventsRes, coachesRes, locationsRes] = await Promise.all([
        calendarAPI.getLessons(params),
        coachesAPI.getAll({}),
        locationsAPI.getAll({}),
      ]);
      
      setEvents(eventsRes.data.map(e => ({
        ...e,
        backgroundColor: e.color,
        borderColor: e.color,
      })));
      setCoaches(coachesRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [filterCoach, filterLocation]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEventClick = (clickInfo) => {
    const event = events.find(e => e.id === clickInfo.event.id);
    if (event) {
      setSelectedEvent(event);
      setRescheduleDate(event.start.split('T')[0]);
      setRescheduleCoach(event.coach_id || '');
      setRescheduleLocation(event.location_id || '');
      setDialogOpen(true);
    }
  };

  const handleDateChange = (info) => {
    const startDate = info.startStr.split('T')[0];
    const endDate = info.endStr.split('T')[0];
    loadData(startDate, endDate);
  };

  const handleReschedule = async () => {
    if (!selectedEvent || !rescheduleDate) return;
    
    setSubmitting(true);
    try {
      await calendarAPI.rescheduleLesson(
        selectedEvent.id,
        rescheduleDate,
        rescheduleCoach !== selectedEvent.coach_id ? rescheduleCoach : null,
        rescheduleLocation !== selectedEvent.location_id ? rescheduleLocation : null
      );
      toast.success('Lesson rescheduled successfully');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to reschedule lesson');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteLesson = async () => {
    if (!selectedEvent) return;
    
    setSubmitting(true);
    try {
      await lessonsAPI.completeWithNotification(selectedEvent.id);
      toast.success('Lesson marked as complete');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to complete lesson');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReminder = async () => {
    if (!selectedEvent) return;
    
    setSubmitting(true);
    try {
      const { emailNotificationsAPI } = await import('../lib/api');
      await emailNotificationsAPI.sendLessonReminder(selectedEvent.id);
      toast.success('Reminder sent successfully');
    } catch (error) {
      toast.error('Failed to send reminder');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>;
      case 'scheduled':
        return <Badge className="bg-amber-100 text-amber-700">Scheduled</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700">Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Lesson Calendar</h1>
          <p className="text-slate-500">View and manage lesson schedule</p>
        </div>
        <div className="flex gap-3">
          <Select value={filterCoach} onValueChange={setFilterCoach}>
            <SelectTrigger className="w-40" data-testid="calendar-filter-coach">
              <SelectValue placeholder="All Coaches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Coaches</SelectItem>
              {coaches.map((coach) => (
                <SelectItem key={coach.id} value={coach.id}>{coach.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterLocation} onValueChange={setFilterLocation}>
            <SelectTrigger className="w-40" data-testid="calendar-filter-location">
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500"></div>
          <span className="text-sm text-slate-600">Scheduled</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
          <span className="text-sm text-slate-600">Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span className="text-sm text-slate-600">Cancelled</span>
        </div>
      </div>

      {/* Calendar */}
      <Card>
        <CardContent className="p-4">
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            events={events}
            eventClick={handleEventClick}
            datesSet={handleDateChange}
            height="auto"
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              meridiem: 'short'
            }}
            eventContent={(eventInfo) => (
              <div className="p-1 text-xs overflow-hidden">
                <div className="font-medium truncate">{eventInfo.event.title}</div>
              </div>
            )}
          />
        </CardContent>
      </Card>

      {/* Event Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Lesson Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              {/* Status */}
              <div className="flex justify-between items-center">
                <span className="font-medium">Status</span>
                {getStatusBadge(selectedEvent.status)}
              </div>

              {/* Student */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                  {selectedEvent.student_photo ? (
                    <img src={selectedEvent.student_photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-primary" />
                  )}
                </div>
                <div>
                  <p className="font-medium">{selectedEvent.student_name}</p>
                  <p className="text-sm text-slate-500">Lesson #{selectedEvent.lesson_number} • {selectedEvent.program_name}</p>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">Coach: {selectedEvent.coach_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">Location: {selectedEvent.location_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">Date: {selectedEvent.start?.split('T')[0]}</span>
                </div>
              </div>

              {/* Reschedule Form (for scheduled lessons) */}
              {canEdit() && selectedEvent.status === 'scheduled' && (
                <div className="border-t pt-4 space-y-3">
                  <h4 className="font-medium text-sm">Reschedule Lesson</h4>
                  <div className="space-y-2">
                    <Label>New Date</Label>
                    <Input
                      type="date"
                      value={rescheduleDate}
                      onChange={(e) => setRescheduleDate(e.target.value)}
                      data-testid="reschedule-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Coach (optional)</Label>
                    <Select value={rescheduleCoach} onValueChange={setRescheduleCoach}>
                      <SelectTrigger>
                        <SelectValue placeholder="Keep current" />
                      </SelectTrigger>
                      <SelectContent>
                        {coaches.map((coach) => (
                          <SelectItem key={coach.id} value={coach.id}>{coach.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Location (optional)</Label>
                    <Select value={rescheduleLocation} onValueChange={setRescheduleLocation}>
                      <SelectTrigger>
                        <SelectValue placeholder="Keep current" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <DialogFooter className="flex-col sm:flex-row gap-2">
                {canEdit() && selectedEvent.status === 'scheduled' && (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleSendReminder}
                      disabled={submitting}
                      className="w-full sm:w-auto"
                      data-testid="send-reminder-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send Reminder
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleReschedule}
                      disabled={submitting}
                      className="w-full sm:w-auto"
                      data-testid="reschedule-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Reschedule
                    </Button>
                    <Button
                      onClick={handleCompleteLesson}
                      disabled={submitting}
                      className="w-full sm:w-auto"
                      data-testid="complete-from-calendar-btn"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Complete
                    </Button>
                  </>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LessonCalendar;
